import { useEffect, useMemo } from 'react'
import { X, ChevronLeft, ChevronRight, Car, ArrowUpRight, CalendarDays } from 'lucide-react'
import { shiftTypeTone, relabelBranch, isLeaveRow, fmtMoney } from '../../lib/clockin.js'
import { indexBranches, positionShort } from '../../lib/employee.js'
import { windowOf } from '../../lib/employeeSchedule.js'
import { fmtMonthTH } from '../../lib/timetable.js'
import { monthDays, DOW_TH, personStats, predictType } from '../../lib/employeeMatrix.js'

/**
 * One person's whole month, every branch — opened from their name in the matrix.
 *
 * `rows` is the cross-branch lookup the grid already holds (lib/crossBranch.js),
 * so this costs no request of its own. That is also what makes it worth having:
 * the matrix can only draw the branch on screen, and a roster question about a
 * person ("why only 21 shifts?") is usually answered by the days they spent
 * somewhere else.
 *
 * EVERY day of the month is a row, including the ones with nothing on them. A
 * blank day is the finding — it is how a person rostered twelve days in a month
 * where everyone else has twenty-five becomes visible — and a list that shows
 * only what exists can never show it. The ลงเวลา report spines its dates the
 * same way and for the same reason.
 *
 * Roster only: the punch (สาย / OT / ออกก่อน) belongs to ลงเวลา, and showing it
 * here would invite an edit this drawer cannot make.
 */
export default function EmployeeMonthDrawer({
  person, rows, ym, branchId, branches = [], onClose, onPrev, onNext, onEdit, onCreate,
}) {
  const branchName = useMemo(() => indexBranches(branches), [branches])
  const days = useMemo(() => monthDays(ym.year, ym.month), [ym])

  const byDate = useMemo(() => {
    const m = new Map()
    for (const r of rows || []) m.set(String(r.date || '').slice(0, 10), r)
    return m
  }, [rows])

  const stats = useMemo(() => personStats({ rows: rows || [] }), [rows])
  const free = days.length - (rows?.length || 0)

  // What they actually worked, most-worked first — the shape of the month in
  // one line, and the answer to "is this an assistant or a counter this month".
  const byType = useMemo(() => {
    const m = new Map()
    for (const r of rows || []) {
      if (isLeaveRow(r)) continue
      m.set(r.type, (m.get(r.type) || 0) + 1)
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [rows])

  const byBranch = useMemo(() => {
    const m = new Map()
    for (const r of rows || []) {
      if (isLeaveRow(r)) continue
      m.set(Number(r.branch_id), (m.get(Number(r.branch_id)) || 0) + 1)
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [rows])

  const travel = useMemo(
    () => (rows || []).reduce((n, r) => n + (r.travel_fee ? Number(r.travel_fee_amount) || 0 : 0), 0),
    [rows],
  )

  /* The longest unbroken run of working days. Leave and empty days both break
   * it — a run is what someone actually worked without a day off, which is the
   * number a fairness or fatigue question is really about. */
  const run = useMemo(() => {
    let best = { len: 0, from: '', to: '' }
    let len = 0
    let from = ''
    for (const d of days) {
      const r = byDate.get(d.date)
      if (r && !isLeaveRow(r)) {
        if (!len) from = d.date
        len += 1
        if (len > best.len) best = { len, from, to: d.date }
      } else {
        len = 0
      }
    }
    return best
  }, [days, byDate])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
      if (e.key === 'ArrowUp') { e.preventDefault(); onPrev?.() }
      if (e.key === 'ArrowDown') { e.preventDefault(); onNext?.() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, onPrev, onNext])

  const homeBranch = relabelBranch(branchName.get(Number(person?.employee?.branch_id)) || '')
  const defaultType = predictType(person)

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[560px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-slate-900 truncate">{person?.code}</h2>
            <p className="text-[12.5px] text-slate-500 truncate">
              {[positionShort(person?.position), homeBranch && `ประจำ${homeBranch}`, fmtMonthTH(ym.year, ym.month)]
                .filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onPrev} title="คนก่อนหน้า (↑)"
              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><ChevronLeft size={16} /></button>
            <button onClick={onNext} title="คนถัดไป (↓)"
              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><ChevronRight size={16} /></button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        {/* สรุป — the month in numbers, before the day-by-day */}
        <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/60 space-y-1.5 text-[12.5px]">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-[15px] font-semibold text-slate-900 tabular-nums">{stats.shifts} เวร</span>
            {stats.personal > 0 && <span className="text-amber-700 tabular-nums">ลากิจ {stats.personal}</span>}
            {stats.sick > 0 && <span className="text-rose-700 tabular-nums">ลาป่วย {stats.sick}</span>}
            {stats.unpaid > 0 && <span className="text-stone-600 tabular-nums">Leave without pay {stats.unpaid}</span>}
            <span className="text-slate-400 tabular-nums">ว่าง {free}</span>
          </div>
          {byType.length > 0 && (
            <div className="text-slate-500">
              {byType.map(([t, n]) => `${t} ${n}`).join(' · ')}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-500">
            {byBranch.map(([id, n]) => (
              <span key={id} className={Number(id) === Number(branchId) ? '' : 'text-amber-700'}>
                {relabelBranch(branchName.get(Number(id)) || '') || `#${id}`} {n}
              </span>
            ))}
            {stats.otAllow > 0 && <span className="tabular-nums">อนุญาต OT {stats.otAllow}</span>}
            {stats.otSpecial > 0 && <span className="tabular-nums">OT พิเศษ {stats.otSpecial}</span>}
            {travel > 0 && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <Car size={12} /> {fmtMoney(travel)}
              </span>
            )}
          </div>
          {run.len >= 6 && (
            <div className="text-[12px] text-amber-700">
              ทำงานติดกัน {run.len} วัน ({run.from.slice(8)}–{run.to.slice(8)} {fmtMonthTH(ym.year, ym.month).split(' ')[0]})
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {days.map((d) => (
            <DayRow key={d.date} day={d} row={byDate.get(d.date)} branchId={branchId}
              branchName={branchName}
              onClick={() => {
                const r = byDate.get(d.date)
                if (r) onEdit?.(r)
                else onCreate?.(d.date, person.id, defaultType)
              }} />
          ))}
        </div>
      </div>
    </div>
  )
}

function DayRow({ day, row, branchId, branchName, onClick }) {
  const leave = row && isLeaveRow(row)
  const w = row ? windowOf(row) : null
  const elsewhere = row && Number(row.branch_id) !== Number(branchId)
  // A missing window is shown as a gap, never as the branch's standard hours:
  // `start_date` is a private column and does not always survive the API, and a
  // guess printed as a record is worse than an honest dash.
  const windowLabel = !row ? '' : leave ? 'ทั้งวัน' : (w.start && w.end ? `${w.start}–${w.end}` : '—')

  return (
    <button onClick={onClick}
      className={`w-full flex items-baseline gap-3 px-6 py-2 text-left border-b border-slate-50 hover:bg-brand-50/40 transition
        ${day.weekend ? 'bg-slate-50/50' : ''}`}>
      <span className={`w-[42px] shrink-0 tabular-nums text-[12px] ${day.weekend ? 'text-slate-400' : 'text-slate-500'}`}>
        {DOW_TH[day.dow]} {day.day}
      </span>

      {!row ? (
        <span className="text-[12.5px] text-slate-300">—</span>
      ) : (
        <>
          <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11.5px] font-medium ring-1 ${shiftTypeTone(row.type)}`}>
            {row.type}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px] text-slate-500">
              {!leave && (
                <span className={elsewhere ? 'inline-flex items-center gap-0.5 text-amber-700' : ''}>
                  {elsewhere && <ArrowUpRight size={11} />}
                  {relabelBranch(branchName.get(Number(row.branch_id)) || '') || '—'}
                </span>
              )}
              <span className="tabular-nums">{windowLabel}</span>
              {row.travel_fee && (
                <span className="inline-flex items-center gap-0.5 text-emerald-700 tabular-nums">
                  <Car size={11} /> {fmtMoney(row.travel_fee_amount)}
                </span>
              )}
              {row.allow_OT === true && <span className="text-slate-400">•OT</span>}
              {row.special_OT === true && <span className="text-slate-400">★OT พิเศษ</span>}
            </span>
            {row.remark && (
              <span className="block text-[12px] text-slate-400 break-words mt-0.5">{row.remark}</span>
            )}
          </span>
        </>
      )}

      {!row && (
        <span className="ml-auto shrink-0 text-slate-300"><CalendarDays size={13} /></span>
      )}
    </button>
  )
}
